import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { httpsCallable } from 'firebase/functions';
// components
import { User } from 'lucide-react';
import FormProvider, { RHFTextField } from '../../components/hook-form';
import { Button } from '../../components/ui/Button';
// auth
import { CLOUD_FUNCTIONS } from '../../auth/FirebaseContext';
// config
import { brandConfig } from '../../config';
// utils
import { cloud_upsertUserCredential } from '../../utils/owner-portal/cloudFunctions';
import { isCustomDomainMode } from '../../utils/customDomainUtils';
import { useLoginFlow, SignInMethod } from '../../auth/LoginProvider';
import { safelyReadErrorMessage } from '../../utils/owner-portal/errorHandling';
import { EMAIL_REGEX } from '../../utils/owner-portal/uiConstants';
import { validatePhoneNumber } from '../../utils/owner-portal/phone/phone';
import { ContactUsPopover } from '../../components/contact-us/ContactUsPopover';

// ----------------------------------------------------------------------

interface VerifyCredentialFormFieldValues {
  emailOrPhone: string;
}

type VerifyCredentialFormProps = {
  next: (method: string) => void;
};

// ----------------------------------------------------------------------

export function VerifyCredentialForm({ next }: VerifyCredentialFormProps) {
  const { setEmailToVerify, setPhoneToVerify, setSignInMethod } = useLoginFlow();

  const VerifyCredentialSchema = Yup.object().shape({
    emailOrPhone: Yup.string()
      .required('Phone number or email required')
      .test(
        'valid-email-or-phone', // test name
        'The phone number or email must be valid.',
        (value) => {
          const validAsEmail = value.match(EMAIL_REGEX) !== null;
          const phoneValidation = validatePhoneNumber(value, 'US');
          const validCredential = validAsEmail || phoneValidation.success;
          return validCredential;
        }
      )
  });

  const defaultValues = {
    emailOrPhone: ''
  };

  const methods = useForm<VerifyCredentialFormFieldValues>({
    resolver: yupResolver(VerifyCredentialSchema),
    defaultValues,
    mode: 'onChange'
  });

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting, isValid }
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const request = httpsCallable<{ credential: string; brand: string }, Record<string, any>>(
        CLOUD_FUNCTIONS,
        cloud_upsertUserCredential
      );
      const verificationResponse = await request({
        credential: data.emailOrPhone,
        brand: brandConfig.brandCode
      });
      const { method, email, phone, success, error } = verificationResponse.data;

      if (error) {
        setError('emailOrPhone', { message: 'Please try again.' });
        return;
      }

      if (!success) {
        setError('emailOrPhone', { message: 'Account not found. Please contact support.' });
        return;
      }

      // Handle response based on authentication method
      if (method === 'email') {
        setEmailToVerify(email || data.emailOrPhone);
        setSignInMethod(SignInMethod.Email);
      } else if (method === 'phone') {
        const phoneNumber = phone || data.emailOrPhone;
        setPhoneToVerify(phoneNumber);
        setSignInMethod(SignInMethod.Phone);
      }

      const customDomainMode = isCustomDomainMode();

      if (customDomainMode && method === 'email') {
        // In custom domain mode, email tokens are sent via email
        // The user will receive a custom token email and click the link
        next(method);
        return;
      }

      // Standard verification flow (email or phone)
      next(method);
    } catch (caught: any) {
      if (
        caught?.code === 'functions/failed-precondition' &&
        caught?.message?.includes('Email links not supported')
      ) {
        setError('emailOrPhone', {
          message: caught.message
        });
      } else if (caught?.code === 'functions/not-found') {
        // Show the specific message from the cloud function for not-found errors
        setError('emailOrPhone', {
          message: caught?.message || 'Account not found. Please contact your administrator.'
        });
      } else if (
        caught?.code === 'functions/unauthenticated' ||
        caught?.code === 'functions/permission-denied' ||
        caught?.code === 'functions/internal'
      ) {
        setError('emailOrPhone', {
          message: 'Authentication service error. Please try again later.'
        });
      } else {
        setError('emailOrPhone', { message: safelyReadErrorMessage(null, caught) });
      }
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <div className="space-y-5">
        <WelcomeText greeting={brandConfig.greeting} />

        <RHFTextField
          disabled={isSubmitting}
          name="emailOrPhone"
          label="Phone or email"
          type="text"
          autoFocus
          endAdornment={<User className="h-4 w-4 text-muted-foreground" />}
        />

        <div className="flex flex-col space-y-3">
          <Button
            variant="default"
            disabled={isSubmitting || !isValid}
            loading={isSubmitting}
            type="submit"
            className="h-9 px-4 w-full font-semibold"
          >
            Continue
          </Button>

          <div className="flex justify-start">
            <HelpLink />
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

//-----------------

function WelcomeText({ greeting }: { greeting: string }) {
  return (
    <div className="space-y-1">
      <h1 className="text-xl font-semibold tracking-tight text-foreground">{greeting}!</h1>
      <p className="text-sm text-muted-foreground">
        Please enter your phone number or email address to continue.
      </p>
    </div>
  );
}

function HelpLink() {
  return (
    <ContactUsPopover
      trigger={
        <button
          type="button"
          className="text-sm text-primary hover:text-primary dark:text-secondary/90 hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
        >
          Help?
        </button>
      }
    />
  );
}
